import { useCallback, useState } from "react";
import { sharedLib } from "@shared/services/shared.service.ts";
import { DELETE_EXTERNAL_INVITATION_CACHE_KAY } from "features/configuration/InviteExternalUsers/constants.ts";
import {
  copyCodeToClipboard,
  displayResultMessage,
  openMessage,
} from "utils/misc.tsx";
import { getCopyMessage } from "@shared/common/CopyToClipboard/utils.ts";
import CopyOutlined from "@ant-design/icons/CopyOutlined";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import { ExternalAccountInvitation, LanguageNameSpaces } from "@shared/types";
import useQuery from "utils/hooks/useQuery.ts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useRouteMatch } from "react-router-dom";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  EXTERNAL_INVITE_PATH,
  NEW_PERMISSION_ACCOUNT_INVITE_FEATURE_FLAG,
} from "common/constants.ts";
import { EXTERNAL_ACCOUNT_INVITE_CLIENT_TOKEN_QUERY_PARAM } from "@shared/common/constants.ts";
import { useSelectUser } from "@shared/utils/hooks/misc.ts";
import { SubscriptionPlanType } from "@shared/types";
import { getWebsiteBaseUrl } from "app/shared/utils.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";

export const useExternalInviteToken = () => {
  const query = useQuery();
  return (
    query.get(EXTERNAL_ACCOUNT_INVITE_CLIENT_TOKEN_QUERY_PARAM) ?? undefined
  );
};

export const useExternalInviteData = () => {
  const isExternalInvitePage = useRouteMatch(EXTERNAL_INVITE_PATH);
  const externalInviteToken = useExternalInviteToken();
  const getAccountInviteStatus =
    sharedLib.sharedApi.configuration.useGetAccountInviteQuery(
      externalInviteToken ? { authToken: externalInviteToken } : skipToken
    );

  return {
    getAccountInviteStatus,
    inviteData: getAccountInviteStatus.data,
    externalInviteToken,
    isExternalInvitePage,
  };
};

export const useShowPermissionOptions = () => {
  const user = useSelectUser();
  const isAssetManager = user?.isAssetManager();
  const isCommunityAdmin =
    user?.subscription_plan_type === SubscriptionPlanType.COMMUNITY_ADMIN;
  const hasWritePermissionFlag = user?.hasFeatureFlag(
    NEW_PERMISSION_ACCOUNT_INVITE_FEATURE_FLAG
  );
  return {
    showClientPermissionOptions: isAssetManager,
    showAssetManagerPermissionOptions: isAssetManager && hasWritePermissionFlag,
    isDefaultPermissions:
      !isCommunityAdmin && !isAssetManager && !hasWritePermissionFlag,
    isCommunityAdmin,
    isAssetManager,
    hasWritePermissionFlag,
  };
};

export const useGetExternalInviteLink = () => {
  const { brandingData } = useBranding();

  const getExternalInviteLink = useCallback(
    (token?: string, primaryExchange?: string) => {
      const baseUrl = `${
        brandingData?.cname ?? getWebsiteBaseUrl()
      }${EXTERNAL_INVITE_PATH}/?${EXTERNAL_ACCOUNT_INVITE_CLIENT_TOKEN_QUERY_PARAM}=${token}`;
      if (primaryExchange) return `${baseUrl}&exchange=${primaryExchange}`;
      return baseUrl;
    },
    [brandingData]
  );

  return {
    getExternalInviteLink,
  };
};

export const useInvitationActions = (invitation: ExternalAccountInvitation) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { getExternalInviteLink } = useGetExternalInviteLink();

  const deleteAccountInvite =
    sharedLib.sharedApi.configuration.useDeleteAccountInviteMutation({
      fixedCacheKey: DELETE_EXTERNAL_INVITATION_CACHE_KAY,
    })[0];

  const onDeleteClick = () => {
    displayResultMessage(
      deleteAccountInvite({ token: invitation.token }).unwrap()
    ).catch(() => {});
  };

  const onCopyLinkClick = () => {
    copyCodeToClipboard(getExternalInviteLink(invitation.token)).catch(
      () => {}
    );
    openMessage(getCopyMessage(t("invitations.invite_link")), "info");
  };

  const actionsItems = [
    {
      key: "copy",
      icon: <CopyOutlined />,
      label: t("invitations.copy_link"),
      onClick: onCopyLinkClick,
    },
    {
      key: "delete",
      icon: <DeleteOutlined />,
      label: t("invitations.delete"),
      onClick: onDeleteClick,
    },
  ];

  return {
    actionsItems,
    editModalOpen,
    setEditModalOpen,
  };
};
